import React, { useState, useEffect } from 'react';
import { Radio, Users, Calendar, Headphones, MessageSquarePlus, Music, Clock, Disc3 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import EngagementCard from '@/components/EngagementCard';
import AdsCard from '@/components/AdsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [recentTracks, setRecentTracks] = useState([]);
  const [pendingRequests, setPendingRequests] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/stats`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/radio/nowplaying`).then(r => setNowPlaying(r.data)).catch(() => {});
    axios.get(`${API}/radio/recent`).then(r => setRecentTracks(r.data.recent || [])).catch(() => {});
    axios.get(`${API}/admin/requests?status=pending`).then(r => setPendingRequests(r.data.counts?.pending || 0)).catch(() => {});
  }, []);

  // Refresh now playing every 10s
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/radio/nowplaying`).then(r => setNowPlaying(r.data)).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const statCards = [
    { label: 'Listeners Now', value: nowPlaying?.listeners || 0, icon: Headphones, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
    { label: 'Shows', value: stats?.shows || 0, icon: Radio, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    { label: 'Pending Requests', value: pendingRequests, icon: MessageSquarePlus, color: 'text-amber-400', bg: 'bg-amber-400/10' },
    { label: 'Schedule Entries', value: stats?.schedule_entries || 0, icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Welcome back, {user?.name || 'Admin'}</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, i) => (
          <div
            key={card.label}
            data-testid={`stat-card-${i}`}
            className="bg-[#171A21] rounded-2xl border border-white/5 p-6 hover:-translate-y-0.5 transition-transform"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-2.5 rounded-xl ${card.bg}`}>
                <card.icon size={20} className={card.color} />
              </div>
            </div>
            <p className="text-3xl font-semibold text-white">{card.value}</p>
            <p className="text-[#94A3B8] text-sm mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Engagement (consented analytics) */}
        <EngagementCard />

        {/* Ad performance */}
        <AdsCard />

        {/* Now Playing */}
        <div data-testid="now-playing-card" className="bg-[#171A21] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-2 h-2 bg-red-500 rounded-full live-pulse" />
            <h2 className="text-lg font-semibold text-white">Now Playing</h2>
          </div>
          {nowPlaying?.title ? (
            <div className="flex items-start gap-4">
              {nowPlaying.cover_url && (
                <img
                  src={`${nowPlaying.cover_url}?${Math.floor(Date.now() / 10000)}`}
                  alt="Cover"
                  className="w-20 h-20 rounded-xl object-cover shrink-0"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="min-w-0">
                <p className="text-white text-xl font-semibold truncate">{nowPlaying.title}</p>
                <p className="text-[#D4AF37] text-sm mt-1">{nowPlaying.artist}</p>
                {nowPlaying.next_title && (
                  <p className="text-[#94A3B8] text-xs mt-3">
                    Up next: <span className="text-white/70">{nowPlaying.next_artist} — {nowPlaying.next_title}</span>
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-[#94A3B8] text-sm">Stream data unavailable</p>
          )}
        </div>

        {/* Recent Tracks from Stream */}
        <div data-testid="recent-tracks-card" className="bg-[#171A21] rounded-2xl border border-white/5 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Clock size={18} className="text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-white">Recently Played</h2>
          </div>
          {recentTracks.length > 0 ? (
            <div className="space-y-2">
              {recentTracks.map((track, i) => (
                <div key={i} data-testid={`recent-track-${i}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center shrink-0">
                    <Music size={14} className="text-[#D4AF37]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">{track.title}</p>
                    <p className="text-[#94A3B8] text-xs truncate">{track.artist}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#94A3B8] text-sm">No recent tracks available</p>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-[#171A21] rounded-2xl border border-white/5 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Manage Shows', href: '/admin/shows', icon: Radio },
            { label: 'View Schedule', href: '/admin/schedule', icon: Calendar },
            { label: 'Song Requests', href: '/admin/requests', icon: MessageSquarePlus },
            { label: 'Manage Hosts', href: '/admin/hosts', icon: Users },
          ].map(link => (
            <a key={link.href} href={link.href} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] text-[#94A3B8] transition-colors text-center">
              <link.icon size={20} />
              <span className="text-xs font-medium">{link.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
